import { ReactElement, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState, ThunkDispatchType, GlobalModalComponentName, WeekStartDay } from "../../../store";
import Header from "./Header";
import {
  selectTimezone, selectSelectedAdditionalCalendars, selectDateRange, selectCurrentOrNewMeeting,
  updateDateRange,
  showPendingSlotsUpdated,
  showAllDayEventsUpdated,
  selectActiveTimezones
} from "../../../store/scheduleUI";
import { useHandler } from "../../../store/hooks";
import { calendarToParticipant } from "../../../utils/scheduleUtils";
import { scheduling } from "../../../store/cabinetApi/scheduling";
import { View as RBCView } from "react-big-calendar";


export interface ScheduleHeaderProps {
  onToggleSidePanel: () => void;
  alertMessageOverride?: string | null;
}


const ScheduleHeaderContainer = ({
  onToggleSidePanel,
  alertMessageOverride,
}: ScheduleHeaderProps): ReactElement | null => {
  const user = useSelector((state: RootState) => state.auth.user);
  const calendarTimezone = useSelector(selectTimezone);
  const activeTimezones = useSelector(selectActiveTimezones);
  const selectedLeaders = useSelector((state: RootState) => state.scheduleUI.selectedLeaderIds);
  const selectedAdditionalCalendars = useSelector(selectSelectedAdditionalCalendars);
  const currentDateRange = useSelector(selectDateRange);
  const currentOrNewMeeting = useSelector(selectCurrentOrNewMeeting);
  const showPendingSlots = useSelector((state: RootState) => state.scheduleUI.showPendingSlots);
  const showAllDayEvents = useSelector((state: RootState) => state.scheduleUI.showAllDayEvents);
  const leadersLoaded = useSelector((state: RootState) => state.leaders.loaded);
  
  const { data: schedulingPrefs } = scheduling.useAppSchedulingPreferencesListQuery();
  const [updateSchedulingPrefs] = scheduling.useAppSchedulingPreferencesPartialUpdateMutation();
  
  const userPrefs = schedulingPrefs?.user_prefs;

  const dispatch = useDispatch<ThunkDispatchType>();
  const handle = useHandler();

  const openUpgradeModal = () => dispatch(actions.globalModal.openModal(GlobalModalComponentName.CABINET_PROMO));

  const showSecondaryTimezone = user?.features.MULTI_TIMEZONE && userPrefs?.multi_timezone;
  const creatingMeeting = currentOrNewMeeting?.id === -1;

  // Convert week_view string to RBCView enum
  const calendarView: RBCView = userPrefs?.week_view === "WEEK" ? "week" as RBCView
    : userPrefs?.week_view === "DAY" ? "day" as RBCView
      : "work_week" as RBCView; // Default to work_week

  const handleToggleMultiTimezone = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          multi_timezone: !userPrefs.multi_timezone,
        }
      });
    }
  };

  const handleToggleGranularTimeSelection = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          granular_time_selection: !userPrefs.granular_time_selection,
        }
      });
    }
  };

  const handleToggleCanceledDeclinedMeetings = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          show_canceled_declined_events: !userPrefs.show_canceled_declined_events,
        }
      });
    }
  };

  const handleToggleShowAllRecurringSlots = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          show_all_recurring_slots: !userPrefs.show_all_recurring_slots,
        }
      });
    }
  };

  const handleNewMeetingClick = useCallback((poll = false, reusable = false, recurring = false) => {
    if (!leadersLoaded) return;
    dispatch(actions.schedule.createNewMeeting(
      selectedLeaders,
      selectedAdditionalCalendars.map((cal, idx) => calendarToParticipant(-1, cal, (idx + 1) * -1)),
      userPrefs,
      poll,
      reusable,
      false,
      recurring,
    ));
  }, [selectedAdditionalCalendars, dispatch, selectedLeaders, leadersLoaded, userPrefs]);

  const handleCreateOneOffMeeting = useCallback(() => {
    handleNewMeetingClick();
  }, [handleNewMeetingClick]);

  const handleCreatePoll = useCallback(() => {
    handleNewMeetingClick(true);
  }, [handleNewMeetingClick]);

  const handleCreateReusableMeeting = useCallback(() => {
    handleNewMeetingClick(false, true);
  }, [handleNewMeetingClick]);

  const handleCreateRecurringMeeting = useCallback(() => {
    handleNewMeetingClick(false, false, true);
  }, [handleNewMeetingClick]);

  const handleToggleShowPendingSlots = useCallback(() => {
    dispatch(showPendingSlotsUpdated(!showPendingSlots));
  }, [dispatch, showPendingSlots]);

  const handleToggleCondensePendingSlots = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          condense_pending_slots: !userPrefs.condense_pending_slots,
        }
      });
    }
  };

  const handleToggleShowAllDayEvents = useCallback(() => {
    dispatch(showAllDayEventsUpdated(!showAllDayEvents));
  }, [dispatch, showAllDayEvents]);

  const handleToggleShowMultiLeaderColumns = async () => {
    if (userPrefs) {
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          show_multi_leader_columns: !userPrefs.show_multi_leader_columns,
        }
      });
    }
  };

  const handleCalendarViewChange = async (view: RBCView) => {
    if (userPrefs) {
      // Convert RBCView to week_view string
      const weekView = view === "week" ? "WEEK" 
        : view === "day" ? "DAY" 
          : "WORK_WEEK";
      
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          week_view: weekView,
        }
      });
    }
  };

  if (!user) return null;

  return (
    <Header
      creatingMeeting={creatingMeeting}
      currentMeetingId={currentOrNewMeeting?.id}
      onCreateMeeting={handleCreateOneOffMeeting}
      onCreateReusableMeeting={handleCreateReusableMeeting}
      onCreateRecurringMeeting={handleCreateRecurringMeeting}
      onCreatePoll={handleCreatePoll}
      calendarTimezoneSelected={calendarTimezone}
      activeTimezones={activeTimezones}
      onDateChange={handle(updateDateRange)}
      currentDateRangeInfo={currentDateRange}
      onToggleSidePanel={onToggleSidePanel}
      onToggleMultiTimezone={handleToggleMultiTimezone}
      onToggleGranularTimeSelection={handleToggleGranularTimeSelection}
      onToggleCanceledDeclinedMeetings={handleToggleCanceledDeclinedMeetings}
      hasMultiLeaderCalendarView={user?.features.MULTI_LEADER_CALENDAR_VIEW}
      hasMultiTimezone={user?.features.MULTI_TIMEZONE}
      granularTimeSelection={userPrefs?.granular_time_selection || false}
      openUpgradeModal={openUpgradeModal}
      showCanceledDeclinedMeetings={userPrefs?.show_canceled_declined_events ?? true}
      showSecondaryTimezone={showSecondaryTimezone}
      showAllRecurringSlots={userPrefs?.show_all_recurring_slots ?? false}
      onToggleShowAllRecurringSlots={handleToggleShowAllRecurringSlots}
      calendarView={calendarView}
      onCalendarViewChange={handleCalendarViewChange}
      showPendingSlots={showPendingSlots}
      onToggleShowPendingSlots={handleToggleShowPendingSlots}
      condensePendingSlots={userPrefs?.condense_pending_slots ?? false}
      onToggleCondensePendingSlots={handleToggleCondensePendingSlots}
      showAllDayEvents={showAllDayEvents}
      onToggleShowAllDayEvents={handleToggleShowAllDayEvents}
      showMultiLeaderColumns={userPrefs?.show_multi_leader_columns ?? false}
      onToggleShowMultiLeaderColumns={handleToggleShowMultiLeaderColumns}
      weekStartDay={userPrefs?.week_start_day ? WeekStartDay[userPrefs.week_start_day] : null}
      alertMessageOverride={alertMessageOverride}
    />
  );
};

export default ScheduleHeaderContainer;
